import React from 'react';
import { C, S } from './_overviewTheme';
import OverviewCard from './_OverviewCard';
import {
  formatCents, formatCentsRounded,
  sanitizeMoneyInput, formatMoneyInput, parseDollarsToCents,
} from './_money';

/**
 * src/pages/reports/GoalTracker.js — Card P254.
 *
 * Carlos, as CEO: "you should be able to open this dashboard and answer in
 * under 60 seconds: are we going to hit our monthly revenue goal?" This
 * component answers that one question and nothing else, which is why it sits
 * above everything on the Overview.
 *
 * ⛔⛔ IT COMPUTES NOTHING. Every figure arrives from reports:ceo:goal, which
 * re-serves routes/_finance_summary.js — the path verified to the cent against
 * GHL on 3 Nov — and the pace projection from routes/_goal_tracker.js. The one
 * reason that matters: a percentage recomputed here from a rounded dollar
 * figure disagrees with the server's by a tenth of a point, and the CEO is the
 * person most likely to read both on the same screen and ask which is right.
 */

/* ── C. COLLECTED AND BOOKED ARE NEVER THE SAME WORD ──────────────────────
 *
 * ⛔⛔ THE LABELS ARE CONSTANTS, SHARED BY EVERY SURFACE IN THIS FILE.
 *
 * Carlos's own words were that money received and money invoiced had been
 * reported to him as one figure. They are different questions — one is cash in
 * the bank, the other is a claim on someone else's — and the gap between them
 * is the business's receivables, which is separately $1.5m. A reader who
 * cannot tell from the tile which one they are holding will read "booked" as
 * cash, and the forecast built on it is wrong by the whole of A/R.
 *
 * Spelling them twice is how they drift apart, so they are spelled once.
 */
export const COLLECTED_GLOSS = 'Collected = money received';
export const BOOKED_GLOSS = 'Booked = invoiced, expected';

/** "November 2026" from 'YYYY-MM'. Formatted in UTC: the key is already a
 *  business-zone month, and re-interpreting it in a local zone can shift it. */
export function monthTitle(monthKey) {
  if (typeof monthKey !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(monthKey)) return null;
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * The pace verdict as the words shown on screen.
 *
 * ⚠ RETURNS THE SENTENCE, not a colour. A verdict encoded only in colour is
 * untestable and unreadable to anyone who cannot separate the hues — the
 * finding behind [the allowlist-of-failures control]. Colour is added
 * alongside, never instead.
 */
export function paceVerdict(pace) {
  if (!pace) return null;
  if (pace.status === 'ahead') return `Ahead by ${formatCentsRounded(pace.deltaCents)}`;
  if (pace.status === 'behind') return `Behind by ${formatCentsRounded(Math.abs(pace.deltaCents))}`;
  return 'On pace';
}

function paceTone(pace) {
  if (!pace) return C.muted;
  if (pace.status === 'ahead') return '#15803D';
  if (pace.status === 'behind') return C.high;
  return C.body;
}

/** The method, in the reader's words, with this month's actual numbers in it. */
export function paceMethod(pace, targetCents) {
  if (!pace) return null;
  return `Expected by today = ${formatCentsRounded(targetCents)} × ${pace.daysElapsed} of `
    + `${pace.daysInMonth} days = ${formatCentsRounded(pace.expectedCents)}. `
    + `"On pace" means within one day's share (${formatCentsRounded(pace.toleranceCents)}).`;
}

/**
 * The progress bar.
 *
 * ⚠ THE PACE MARKER IS A SECOND, INDEPENDENT SIGNAL and not decoration: the
 * fill alone says how much came in, which cannot answer "is that enough by
 * now". ⚠ Both are also stated in text above, so the bar is never the only
 * place a figure appears.
 */
function ProgressBar({ attainedPct, pace, targetCents }) {
  const pct = Number.isFinite(attainedPct) ? Math.max(0, Math.min(100, attainedPct)) : 0;
  const expectedPct = pace && targetCents > 0
    ? Math.max(0, Math.min(100, (pace.expectedCents / targetCents) * 100))
    : null;
  return (
    <div
      style={{
        position: 'relative', height: 14, borderRadius: 7, background: C.well,
        /* ⭐ P222 — the outline carries the 3:1 boundary contrast, so the fill
           is free to be the published hue. */
        outline: `1px solid ${C.rule}`, overflow: 'visible', marginTop: 10,
      }}
      role="img"
      aria-label={`${attainedPct === null ? 'unknown' : `${attainedPct}%`} of the monthly target collected`}
      data-testid="goal-progress"
    >
      <div style={{
        width: `${pct}%`, height: '100%', borderRadius: 7,
        background: C.seriesDirect, transition: 'width 200ms ease',
      }}
      />
      {expectedPct === null ? null : (
        <div
          data-testid="goal-pace-marker"
          title="Expected by today"
          style={{
            position: 'absolute', top: -3, bottom: -3, left: `${expectedPct}%`,
            width: 2, background: C.text,
          }}
        />
      )}
    </div>
  );
}

/**
 * ⛔⛔ THE NO-TARGET STATE IS A FIRST-CLASS RENDER, NOT A FALLBACK.
 *
 * It shows "Set a monthly target" — never $0 and never 0%. A zero target
 * makes every month read as infinitely attained or as nothing at all, and
 * both are claims about the business that nobody made. This is the #686
 * three-state rule applied to the one figure the screen exists for.
 */
function NoTarget({ monthLabel, canSetTarget, onSetTarget }) {
  return (
    <div data-testid="goal-no-target">
      <div style={{ fontSize: 15, fontWeight: 700, color: C.muted }}>
        Set a monthly target
      </div>
      <div style={{ fontSize: 12, color: C.faint, marginTop: 6, maxWidth: 560 }}>
        {monthLabel
          ? `No revenue goal has been set for ${monthLabel}, so attainment and pace cannot be shown.`
          : 'No revenue goal has been set for this month, so attainment and pace cannot be shown.'}
        {' '}
        {canSetTarget
          ? 'Enter one below.'
          /* ⚠ Names the permission in plain words, not its key. A reader who
             cannot act needs to know who can, not what the grant is called. */
          : 'A system administrator can set it in the CRM.'}
      </div>
      {canSetTarget && typeof onSetTarget === 'function' ? (
        <button
          type="button" data-testid="goal-set-target"
          onClick={onSetTarget}
          style={{ ...S.h2, marginTop: 12, cursor: 'pointer', color: C.primary,
            background: 'none', border: `1px solid ${C.rule}`, borderRadius: 6, padding: '7px 12px' }}
        >
          Set a monthly target
        </button>
      ) : null}
    </div>
  );
}

/**
 * ⛔⛔ THE SANITY THRESHOLD — Card P258, Daniel's rule 3.
 *
 * More than 3× or less than ⅓ of the basis asks for a second click. The
 * numbers are HIS, not invented here, and the rule NEVER BLOCKS: a genuine
 * tenfold month must still be enterable, and a control that refuses a real
 * value teaches people to route around it.
 */
const SANITY_HIGH = 3;
const SANITY_LOW = 1 / 3;

/** Is `cents` implausibly far from the basis? Null basis ⇒ never. */
export function isFarFromBasis(cents, basisCents) {
  const v = Number(cents);
  const b = Number(basisCents);
  if (!Number.isFinite(v) || !Number.isFinite(b) || b <= 0 || v <= 0) return false;
  return v > b * SANITY_HIGH || v < b * SANITY_LOW;
}

/** "last month's" / "last month's target" — the basis, in words. */
export function basisLabel(sanity) {
  if (!sanity) return null;
  const month = monthTitle(sanity.basisMonth);
  return sanity.basisKind === "previous_target"
    ? `${month || "last month"}'s target`
    : `${month || "last month"}'s collected`;
}

/**
 * The target editor — "the target input must format the number as it's typed
 * so it can't be entered wrong" (Daniel, 3 Nov 2026, Card P258).
 *
 * ⛔⛔ THE FIELD IS DOLLARS, THE WIRE IS CENTS, AND THE READ-BACK IS THE
 * CONTRACT BETWEEN THEM. What the line under the box says is what gets
 * stored — both are rendered from the SAME integer-cents value produced by
 * parseDollarsToCents, which never constructs a float. See _money.js for why
 * `Math.round(n * 100)` is not good enough at these magnitudes.
 *
 * ⚠ THE `$` LIVES OUTSIDE THE INPUT. Inside, it can be deleted, duplicated,
 * or end up mid-value by a stray caret — and then the field holds something
 * the parser has to guess about. Outside, it is furniture.
 */
function TargetEditor({ month, monthLabel, currentCents, sanity, onSave, onCancel }) {
  const [raw, setRaw] = React.useState(
    currentCents === null || currentCents === undefined
      ? '' : formatMoneyInput(String(Math.floor(currentCents / 100)))
  );
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  /* ⚠ Reset whenever the value changes, so a confirmed warning cannot carry
     over to a DIFFERENT number the user typed afterwards. */
  const [confirming, setConfirming] = React.useState(false);

  const cents = parseDollarsToCents(raw);
  const valid = cents !== null && cents > 0;
  const far = valid && isFarFromBasis(cents, sanity && sanity.basisCents);

  const onChange = (e) => {
    setRaw(formatMoneyInput(sanitizeMoneyInput(e.target.value)));
    setConfirming(false);
    setErr(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    /* ⚠ Belt and braces: Save is disabled when invalid, but a form can also
       be submitted with Enter, and a disabled button is a UI state rather
       than a guarantee. */
    if (!valid) { setErr('Enter a dollar amount above $0.'); return; }
    if (far && !confirming) { setConfirming(true); return; }
    setErr(null); setBusy(true);
    try {
      await onSave(month, cents);
    } catch (ex) {
      setErr((ex && ex.message) || 'The target could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const basis = basisLabel(sanity);

  return (
    <form onSubmit={submit} data-testid="goal-target-editor" style={{ marginTop: 12 }}>
      <label htmlFor="goal-target-input"
             style={{ fontSize: 12, fontWeight: 600, color: C.text, display: 'block', marginBottom: 6 }}
      >
        {monthLabel ? `${monthLabel} revenue target` : 'Monthly revenue target'}
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* ⚠ Fixed prefix, OUTSIDE the box — see the header. */}
        <span aria-hidden="true" style={{ fontSize: 16, fontWeight: 700, color: C.body }}>$</span>
        <input
          id="goal-target-input" data-testid="goal-target-input"
          value={raw} onChange={onChange}
          inputMode="decimal" autoComplete="off" disabled={busy}
          aria-label="Monthly revenue target in US dollars"
          aria-describedby={err ? 'goal-target-error' : 'goal-target-readback'}
          aria-invalid={err ? 'true' : undefined}
          style={{ fontSize: 16, padding: '7px 10px', border: `1px solid ${C.rule}`,
            borderRadius: 6, width: 180, color: C.text, background: C.well,
            textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        />
        <button type="submit" data-testid="goal-target-save"
                disabled={busy || !valid}
                style={{ fontSize: 13, fontWeight: 600, padding: '7px 14px',
                  cursor: (busy || !valid) ? 'not-allowed' : 'pointer',
                  opacity: (busy || !valid) ? 0.5 : 1,
                  borderRadius: 6, border: 'none',
                  background: confirming ? C.watchDeep : C.primary, color: '#FFFFFF' }}
        >
          {busy ? 'Saving…' : confirming ? 'Yes, save it' : 'Save target'}
        </button>
        <button type="button" data-testid="goal-target-cancel" onClick={onCancel} disabled={busy}
                style={{ fontSize: 13, padding: '7px 10px', cursor: 'pointer', borderRadius: 6,
                  border: `1px solid ${C.rule}`, background: 'none', color: C.body }}
        >
          Cancel
        </button>
      </div>

      {/*
        * ⛔⛔ THE READ-BACK, AND IT IS THE STORED VALUE. Rendered with
        * formatCents from the very integer the save will send — not from the
        * text in the box — so the sentence cannot drift from the write.
        */}
      <div id="goal-target-readback" data-testid="goal-target-readback"
           style={{ fontSize: 13, color: valid ? C.text : C.faint, marginTop: 8 }}
      >
        {valid
          ? `You are setting ${formatCents(cents)} as the ${monthLabel || 'monthly'} target.`
          : 'Enter a dollar amount above $0.'}
      </div>

      {/*
        * ⚠ A WARNING, NOT A REFUSAL. Daniel: "Never block outright." It asks
        * for one more click and names the figure it is comparing against, so
        * the reader can tell a typo from a real jump.
        */}
      {far ? (
        <div data-testid="goal-target-sanity" role="status"
             style={{ fontSize: 13, color: C.watchDeep, marginTop: 8, maxWidth: 560 }}
        >
          {/* ⚠ basisLabel already carries the possessive ("October 2026's
              collected"); a second &rsquo;s here produced "October 2026's
              collected's $190,000.00" in the evidence shot. */}
          This is far from {basis || 'the previous month'}{' '}
          {formatCents(sanity.basisCents)} — please double-check.
          {confirming
            ? ' Press “Yes, save it” to confirm.'
            : ' Press Save again to confirm.'}
        </div>
      ) : null}

      {err ? (
        <div id="goal-target-error" role="alert" data-testid="goal-target-error"
             style={{ fontSize: 12, color: C.high, marginTop: 6 }}
        >
          {err}
        </div>
      ) : null}
    </form>
  );
}

export default function GoalTracker({
  data, state = 'loading', canSetTarget = false, onSaveTarget = null,
  onOpenCollected = null, onOpenBooked = null, onOpenLeads = null,
  leadsToday = null, leadsTodayState = 'loading',
}) {
  const [editing, setEditing] = React.useState(false);
  const goal = data && data.goal;
  const monthLabel = goal ? monthTitle(goal.month) : null;
  const title = monthLabel ? `${monthLabel} Sales Goal` : 'Monthly Sales Goal';

  const collected = goal && goal.collected;
  const collectedTracked = Boolean(collected && collected.tracked);
  const hasTarget = Boolean(goal && goal.targetCents !== null && goal.targetCents !== undefined);
  /* ⚠ BOTH conditions. The permission alone is not enough — without a save
     handler wired the control would be an affordance that does nothing. */
  const editable = Boolean(canSetTarget && typeof onSaveTarget === 'function');

  const saveTarget = React.useCallback(async (month, cents) => {
    await onSaveTarget(month, cents);
    setEditing(false);
  }, [onSaveTarget]);

  const today = data && data.today;
  const defNeeded = (data && Array.isArray(data.definitionNeeded)) ? data.definitionNeeded : [];

  return (
    <section
      style={{ ...S.card, marginBottom: 16 }}
      data-testid="goal-tracker" data-print-section data-print-keep
    >
      <div style={S.cardHead}>
        <h2 style={{ ...S.h2, fontSize: 15 }} data-testid="goal-title">{title}</h2>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 10 }}>
          {/* ⚠ C — the gloss sits in the header, where it is read before the
              figures rather than after them. */}
          <span style={{ fontSize: 11, color: C.faint }}>{COLLECTED_GLOSS}</span>
          {/* ⚠ Rendered only when the reader can actually write (P167: no
              dead controls) AND a target already exists — with no target the
              no-target block below carries the call to action, and two
              buttons for one job is worse than one. */}
          {editable && hasTarget && !editing ? (
            <button
              type="button" data-testid="goal-edit-target"
              onClick={() => setEditing(true)}
              style={{ fontSize: 11, fontWeight: 600, color: C.primary, background: 'none',
                border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
            >
              Edit target
            </button>
          ) : null}
        </span>
      </div>

      {state === 'loading' ? (
        <div style={{ fontSize: 14, color: C.faint }}>Loading…</div>
      ) : state === 'failed' ? (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.high }}>Could not load</div>
          <div style={{ fontSize: 12, color: C.watchDeep, marginTop: 4 }}>
            The goal tracker did not report. The figures below are not zero — they are unknown.
          </div>
        </div>
      ) : !hasTarget ? (
        <>
          <NoTarget monthLabel={monthLabel} canSetTarget={editable}
                    onSetTarget={() => setEditing(true)} />
          {editable && editing ? (
            <TargetEditor
              month={goal ? goal.month : null} monthLabel={monthLabel}
              currentCents={null} sanity={goal && goal.sanity}
              onSave={saveTarget} onCancel={() => setEditing(false)}
            />
          ) : null}
        </>
      ) : !collectedTracked ? (
        /*
         * ⛔⛔ A SECOND, DIFFERENT ABSENCE. The goal IS set; the payments
         * mirror is what did not report. Showing "Set a monthly target" here
         * would send Carlos to fix the one thing that is not broken.
         */
        <div data-testid="goal-collected-blind">
          <div style={{ fontSize: 15, fontWeight: 700, color: C.muted }}>
            Collected is unavailable
          </div>
          <div style={{ fontSize: 12, color: C.watchDeep, marginTop: 6 }}>
            {(collected && collected.reason)
              || 'The payments mirror did not report, so attainment and pace cannot be shown.'}
            {' '}
            The {monthLabel || 'monthly'} target of {formatCentsRounded(goal.targetCents)} is set.
          </div>
        </div>
      ) : (
        <div data-testid="goal-body">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 30, fontWeight: 700, color: C.text, lineHeight: 1.1 }}
                 data-testid="goal-collected"
            >
              {formatCentsRounded(collected.value)}
            </div>
            {/*
              * ⛔⛔ THE TARGET IS SHOWN TO THE CENT — Card P258 rule 4: the
              * saved value must appear "formatted the same way" as the
              * read-back that preceded it. The big COLLECTED figure beside it
              * stays rounded because it is the scannable one and nobody typed
              * it; the target is the number a human entered and was shown a
              * confirmation of, so rounding it here would mean the panel and
              * the read-back disagree about what was saved — for a target of
              * $260,000.50 the headline would say $260,001.
              */}
            <div style={{ fontSize: 14, color: C.body }} data-testid="goal-target">
              of {formatCents(goal.targetCents)}
            </div>
            {goal.attainedPct === null ? null : (
              <div style={{ fontSize: 14, fontWeight: 700, color: C.primary }}
                   data-testid="goal-attained"
              >
                {goal.attainedPct}%
              </div>
            )}
          </div>

          <ProgressBar
            attainedPct={goal.attainedPct} pace={goal.pace} targetCents={goal.targetCents}
          />

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10, alignItems: 'baseline' }}>
            <div style={{ fontSize: 13, color: C.body }} data-testid="goal-remaining">
              <strong style={{ color: C.text }}>{formatCentsRounded(goal.remainingCents)}</strong>
              {' '}still to collect
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: paceTone(goal.pace) }}
                 data-testid="goal-pace"
            >
              {paceVerdict(goal.pace)}
            </div>
          </div>

          {/* ⚠ THE METHOD IS ON THE SCREEN, in small text, as the card
              requires. A pace verdict whose arithmetic is invisible is a
              number the reader must either trust completely or ignore. */}
          <div style={{ fontSize: 11, color: C.faint, marginTop: 6, maxWidth: 680 }}
               data-testid="goal-method"
          >
            {paceMethod(goal.pace, goal.targetCents)}
          </div>

          {editable && editing ? (
            <TargetEditor
              month={goal.month} monthLabel={monthLabel}
              currentCents={goal.targetCents} sanity={goal.sanity}
              onSave={saveTarget} onCancel={() => setEditing(false)}
            />
          ) : null}
        </div>
      )}

      {/* ── B. TODAY STRIP ──────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${C.rule}`, marginTop: 16, paddingTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 8, flexWrap: 'wrap', marginBottom: 10 }}
        >
          <h3 style={{ ...S.h2, fontSize: 12 }}>Today</h3>
          <span style={{ fontSize: 11, color: C.faint }}>
            {COLLECTED_GLOSS} · {BOOKED_GLOSS}
          </span>
        </div>

        <div style={{ ...S.row, ...S.kpiRow, marginBottom: 0 }}>
          <OverviewCard
            testid="today-collected" label="Collected today"
            onOpen={onOpenCollected}
            state={state === 'loading' ? 'loading'
              : (today && today.collected && today.collected.tracked ? 'ok' : 'untracked')}
            reason={today && today.collected ? today.collected.reason : null}
            value={today && today.collected && today.collected.tracked
              ? formatCents(today.collected.value) : null}
            sub={COLLECTED_GLOSS}
          />
          <OverviewCard
            testid="today-booked" label="Booked today"
            onOpen={onOpenBooked}
            state={state === 'loading' ? 'loading'
              : (today && today.booked && today.booked.tracked ? 'ok' : 'untracked')}
            reason={today && today.booked ? today.booked.reason : null}
            value={today && today.booked && today.booked.tracked
              ? formatCents(today.booked.value) : null}
            sub={BOOKED_GLOSS}
          />
          <OverviewCard
            testid="today-leads" label="New leads today"
            onOpen={onOpenLeads}
            state={leadsTodayState === 'loading' ? 'loading'
              : (Number.isFinite(leadsToday) ? 'ok' : 'untracked')}
            reason={Number.isFinite(leadsToday) ? null : 'The lead count did not report.'}
            value={Number.isFinite(leadsToday) ? String(leadsToday) : null}
            sub="Leads created today"
          />

          {/*
            * ⛔⛔ THE TILES CARLOS ASKED FOR THAT NOBODY HAS DEFINED.
            *
            * They are VISIBLE and they carry NO NUMBER — not a zero, not a
            * dash standing in for one, not a guess. The 28 Oct dashboard
            * showed "Therapies scheduled 0" and a reader could not tell a
            * measured zero from a missing definition; a zero is a claim about
            * the business and this is a claim about the system.
            *
            * ⚠ They render from the SERVER's list, so the day Carlos defines
            * one it is removed in routes/_goal_tracker.js and disappears from
            * here with no change to this file.
            *
            * ⚠ No onOpen: an affordance promising a destination that does not
            * exist is the defect P153 removed from this screen.
            */}
          {defNeeded.map((t) => (
            <OverviewCard
              key={t.key} testid={`today-${t.key}`} label={t.label}
              state="untracked"
              untrackedLabel="Definition needed"
              reason={t.reason}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
